// Hook for fetching products from Firestore
import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  doc,
  getDoc,
  getDocs
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface ProductSize {
  label: string;            // "S", "M", "L", "8", "10", "One Size"
  sku: string;              // Unique: "{productId}_{SIZE}"
  stockCount: number;       // Total units physically available
  reservedCount: number;    // Currently held in active checkouts (15-min TTL)
  committedCount: number;   // Lifetime sold count (for analytics)
  returnedCount: number;    // Lifetime returns (for analytics/abuse detection)
  lowStockAlert: boolean;   // Auto-set when sellable <= threshold
  available: boolean;       // Computed: stockCount - reservedCount > 0
}

export interface Product {
  id: string;
  name: string;
  brand: string;
  brandId: string;
  description: string;
  price: number;
  compareAtPrice?: number | null;
  saleSchedule?: {
    startDate: string;
    endDate: string;
  } | null;
  images: string[];
  sizes: ProductSize[];
  category: string;
  tags: string[];
  active: boolean;
  lowStockThreshold?: number;
  createdAt: Date;
}

// Fetch all products with optional filters
export function useProducts(options?: {
  brandId?: string;
  category?: string;
  limitCount?: number;
}) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let q = query(
      collection(db, 'products'),
      where('active', '==', true),
      orderBy('createdAt', 'desc')
    );

    if (options?.brandId) {
      q = query(q, where('brandId', '==', options.brandId));
    }

    if (options?.category) {
      q = query(q, where('category', '==', options.category));
    }

    if (options?.limitCount) {
      q = query(q, limit(options.limitCount));
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const productsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Product[];
        setProducts(productsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching products:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [options?.brandId, options?.category, options?.limitCount]);

  return { products, loading, error };
}

// Fetch single product by ID
export function useProduct(productId: string | null) {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!productId) {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'products', productId),
      (doc) => {
        if (doc.exists()) {
          setProduct({ id: doc.id, ...doc.data() } as Product);
        } else {
          setProduct(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching product:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [productId]);

  return { product, loading, error };
}

// Search products
export async function searchProducts(searchTerm: string): Promise<Product[]> {
  try {
    // Note: Firestore doesn't support full-text search natively
    // For production, use Algolia or Elasticsearch
    // This is a simple prefix search
    const q = query(
      collection(db, 'products'),
      where('active', '==', true),
      orderBy('name'),
      limit(20)
    );

    const snapshot = await getDocs(q);
    const products = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as Product))
      .filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.brand.toLowerCase().includes(searchTerm.toLowerCase())
      );

    return products;
  } catch (error) {
    console.error('Error searching products:', error);
    return [];
  }
}


